/*
 * Project: bellcloud
 * File: apps/frontend/src/connections/serial/serialAdapter.ts
 * Author: SWAN
 * Description: Web Serial Adapter — 전송(포트 read 루프)만 담당. 프레이밍/응답 패턴은 BaseDeviceAdapter.
 */

import { DeviceFrameRingParser } from '@/connections/deviceFrameRingParser';
import { BaseDeviceAdapter } from '@/connections/baseDeviceAdapter';
import { pollStreamQueue } from '@/connections/pollStreamQueue';
import { BAUD_RATE } from '@packages/protocols/constants';
import { I18nError } from '@packages/types/errors';
import { i18nKeys } from '@/i18n/i18nKeyGen';

interface SerialPortInfo {
  port: string;
  baudRate: number;
  vendorId: number;
  productId: number;
  manufacturer: string;
  serialNumber: string;
  productName: string;
  interfaceClass: number;
}

const MAX_QUEUE_SIZE = 1000; // 스트림 큐 메모리 보호

export class WebSerialAdapter extends BaseDeviceAdapter {
  protected readonly readTimeoutMs = 7000;
  protected readonly noPendingResponseKey = i18nKeys.config.error_serialNoPendingResponse;
  protected readonly logPrefix = '[Serial]';

  private port: SerialPort | null = null;
  private reader: ReadableStreamDefaultReader<any> | null = null;
  private isConnected = false;
  private _isReading = false;
  private isDownloading = false; // 버스트 다운로드 중 다른 요청 차단용

  constructor() {
    super(
      new DeviceFrameRingParser({
        logPrefix: '[Serial]',
        bufferCapacity: 8 * 1024 * 1024,
      })
    );
  }

  get isReading(): boolean {
    return this._isReading;
  }

  get bufferUsage(): number {
    return this.frameParser.internalRingBuffer.getUsage();
  }

  get droppedBytes(): number {
    return this.frameParser.internalRingBuffer.getDroppedBytes();
  }

  /* ---------------- MODE CONTROL ---------------- */
  setSinglePacketMode(): void {
    const wasStreaming = this.mode === 'stream';
    this.mode = 'single';
    this.frameParser.setMode('single');
    this.packetQueue.clear();
    if (wasStreaming) {
      this.isDownloading = false;
    }
  }

  setStreamingMode(): void {
    const wasStreaming = this.mode === 'stream';
    this.mode = 'stream';
    this.frameParser.setMode('stream');
    this.isDownloading = true;

    if (!wasStreaming) {
      // single→stream 전환 시에만 큐 초기화 후, 버퍼에 남은 버스트 프레임을 큐로 drain
      this.packetQueue.clear();
      this.drainBufferIntoQueue();
    }

    if (!this._isReading && this.port?.readable) {
      this.startReading();
    }
  }

  async stopBurstMode(packet: Uint8Array): Promise<Uint8Array> {
    this.isDownloading = false;
    this.setSinglePacketMode();
    await this.write(packet);
    return this.read();
  }

  /* ---------------- CONNECTION ---------------- */
  async scan(): Promise<SerialPortInfo[]> {
    try {
      if (!navigator.serial) return [];
      const ports = await navigator.serial.getPorts();
      return ports.map((port, index) => {
        const info = port.getInfo();
        return {
          port: `port-${index}`,
          baudRate: BAUD_RATE,
          vendorId: info.usbVendorId || 0,
          productId: info.usbProductId || 0,
          manufacturer: '',
          serialNumber: '',
          productName: '',
          interfaceClass: 0,
        };
      });
    } catch (error) {
      console.error('[Serial] Port scan failed:', error);
      return [];
    }
  }

  async connect(_port: string, baudRate: number = BAUD_RATE): Promise<void> {
    try {
      if (!navigator.serial) {
        throw new I18nError(i18nKeys.config.msg_serialNotSupported);
      }

      // 기존 연결 정리
      if (this.reader || this.port) {
        try {
          await this.disconnect();
          await delay(100);
        } catch {
          this._isReading = false;
          this.reader = null;
          this.port = null;
          this.isConnected = false;
        }
      }

      // 크래들 칩셋 필터 (FTDI / Silicon Labs / Prolific)
      const filters = [
        { usbVendorId: 0x0403 },
        { usbVendorId: 0x10c4 },
        { usbVendorId: 0x067b },
      ];
      const selectedPort = await navigator.serial.requestPort({ filters });

      try {
        await selectedPort.open({ baudRate });
      } catch (e) {
        // 이미 열려있거나(InvalidStateError) 사용 중(NetworkError)이면 닫고 재시도
        if (
          e instanceof Error &&
          (e.name === 'InvalidStateError' || e.name === 'NetworkError')
        ) {
          try {
            await selectedPort.close();
            await delay(e.name === 'NetworkError' ? 500 : 300);
            await selectedPort.open({ baudRate });
          } catch (retryError) {
            throw new I18nError(
              i18nKeys.config.msg_serialPortOpenFailed,
              undefined,
              retryError instanceof Error ? retryError : undefined
            );
          }
        } else {
          throw new I18nError(
            i18nKeys.config.msg_serialPortOpenFailed,
            undefined,
            e instanceof Error ? e : undefined
          );
        }
      }

      this.port = selectedPort;
      this.isConnected = true;

      this.frameParser.clear();
      this.pendingPromise = null;
      this.pendingResolver = null;
      this.packetQueue.clear();
      this.mode = 'single';
      this.frameParser.setMode('single');

      this.startReading();
    } catch (error) {
      console.error('[Serial] 연결 실패:', error);
      if (error instanceof I18nError) throw error;
      throw new I18nError(
        i18nKeys.config.error_unknownError,
        undefined,
        error instanceof Error ? error : undefined
      );
    }
  }

  async disconnect(): Promise<void> {
    try {
      this._isReading = false;

      if (this.reader) {
        try {
          await this.reader.cancel();
          await delay(50);
          this.reader.releaseLock();
        } catch (e) {
          console.warn('[Serial] reader 해제 중 오류 (무시):', e);
        } finally {
          this.reader = null;
        }
      }

      if (this.port) {
        try {
          await this.port.close();
        } catch (e) {
          console.warn('[Serial] 포트 닫기 중 오류 (무시):', e);
        } finally {
          this.port = null;
        }
      }

      this.isConnected = false;
      this.resetState();
    } catch (error) {
      console.error('[Serial] 연결 해제 실패:', error);
      this._isReading = false;
      this.reader = null;
      this.port = null;
      this.isConnected = false;
      this.resetState();
      throw error;
    }
  }

  private resetState(): void {
    this.frameParser.clear();
    this.pendingPromise = null;
    this.pendingResolver = null;
    this.packetQueue.clear();
    this.mode = 'single';
    this.frameParser.setMode('single');
  }

  isOpen(): boolean {
    return this.isConnected && this.port !== null;
  }

  /* ---------------- I/O ---------------- */
  async write(data: Uint8Array): Promise<void> {
    if (!this.port || !this.port.writable) {
      throw new I18nError(i18nKeys.config.error_serialPortNotAvailable);
    }

    if (this.isDownloading && this.mode === 'stream') {
      throw new Error('버스트 모드 다운로드 중에는 다른 요청을 보낼 수 없습니다');
    }

    if (this.mode === 'single') {
      this.frameParser.clear();
      this.pendingPromise = null;
      this.pendingResolver = null;
      // 버퍼 클리어가 반영되도록 짧게 양보 후 응답 대기 준비
      await delay(10);
      this.armPendingResponse();
    }

    if (!this._isReading) {
      this.startReading();
    }

    const writer = this.port.writable.getWriter();
    try {
      await writer.write(data);
    } finally {
      writer.releaseLock();
    }
  }

  async readStreamPacket(timeoutMs: number = 30000): Promise<Uint8Array> {
    if (this.mode !== 'stream') {
      throw new Error('Not in streaming mode');
    }
    if (!this._isReading) {
      await this.startReading();
    }
    try {
      return await pollStreamQueue({
        queue: this.packetQueue,
        bufferAvailable: () => this.frameParser.available(),
        timeoutMs,
        logPrefix: this.logPrefix,
      });
    } catch (err) {
      // 읽기 루프가 멈췄으면 재시작 후 명확한 에러로 전환
      if (!this._isReading) {
        if (this.port?.readable) {
          this.startReading().catch(() => {});
        }
        throw new Error('Background read loop stopped');
      }
      throw err;
    }
  }

  clearBuffer(releaseReader: boolean = false): void {
    // 버스트 다운로드 중에는 버퍼 클리어 차단 (간섭 방지)
    if (this.isDownloading && this.mode === 'stream' && !releaseReader) {
      return;
    }

    this.frameParser.clear();
    this.pendingPromise = null;
    this.pendingResolver = null;
    this.packetQueue.clear();

    if (releaseReader) {
      this._isReading = false;
      this.isDownloading = false;
      if (this.reader) {
        this.reader.cancel().catch(() => {});
        this.reader = null;
      }
    }
  }

  /* ---------------- READ LOOP ---------------- */
  async startReading(): Promise<void> {
    if (!this.port || !this.port.readable) {
      console.error('[Serial] 포트가 읽기 가능 상태가 아님');
      return;
    }
    if (this._isReading) return;

    if (this.reader) {
      try {
        await this.reader.cancel();
        await delay(50);
        this.reader.releaseLock();
      } catch {
        /* ignore */
      } finally {
        this.reader = null;
      }
    }

    try {
      const reader = this.port.readable.getReader();
      this.reader = reader;
      this._isReading = true;
      void this.readLoop(reader);
    } catch (error) {
      console.error('[Serial] 읽기 루프 시작 실패:', error);
      this._isReading = false;
    }
  }

  private async readLoop(reader: ReadableStreamDefaultReader<any>): Promise<void> {
    while (this._isReading) {
      try {
        if (this.reader !== reader) break;

        const { value, done } = await reader.read();
        if (done) break;
        if (!value) continue;

        this.frameParser.pushBytes(new Uint8Array(value));

        while (true) {
          // single 모드에서 대기자가 없으면 버퍼를 보존 (setStreamingMode가 drain)
          if (this.mode === 'single' && !this.pendingResolver) break;

          const packet = this.frameParser.extractPacket();
          if (!packet) break;

          if (this.mode === 'single') {
            // 응답 전달 후 중단 — 같은 청크에 버스트가 섞여있어도 버퍼에 남겨둔다
            if (this.dispatchSinglePacket(packet)) break;
          } else {
            if (this.packetQueue.size >= MAX_QUEUE_SIZE) continue; // 과부하 시 drop
            this.packetQueue.push(packet);
          }
        }
      } catch (err) {
        if (
          err instanceof Error &&
          (err.name === 'NetworkError' ||
            err.message.includes('cancel') ||
            err.message.includes('released') ||
            err.message.includes('readable stream reader'))
        ) {
          break;
        }
        await delay(this.mode === 'stream' ? 50 : 100);
      }
    }
    this._isReading = false;
  }
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
